import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';

export interface IncidentSummary {
  id: number;
  key: string;
  title: string;
  summary: string | null;
  pathogen: string | null;
  serotype: string | null;
  vehicle: string | null;
  producer: string | null;
  origin_country: string | null;
  approval_codes: string[];
  countries: string[];
  case_count: number | null;
  hospitalizations: number | null;
  deaths: number | null;
  facility_closure: boolean;
  record_language: boolean;
  status: string;
  notability_score: number;
  t1_triggers: string[];
  first_signal_date: string | null;
  last_item_date: string | null;
  last_event_at: string | null;
  hazard_name: string | null;
  hazard_category: string | null;
  manual_override?: boolean;
  kind?: string;
  root_ingredient?: string | null;
  media_reported_date?: string | null;
  narrative?: string | null;
  elevated_at?: string | null;
  major_at?: string | null;
  case_count_source?: string | null;
  crisis_type?: string | null;
  hazard?: string | null;
  crisis_evidence?: Array<{ marker: string; quote: string; source: string;
                            url: string | null; date: string | null }>;
  judged_at?: string | null;
}

/** With an editor token the API also returns clusters; without, crises only. */
export async function fetchIncidents(status: string, days: number,
                                     token?: string | null): Promise<IncidentSummary[]> {
  const res = await fetch(
    `${API_BASE_URL}/api/incidents?status=${encodeURIComponent(status)}&days=${days}`,
    { cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; incidents: IncidentSummary[] };
  return data.incidents;
}

export function incidentStats(inc: IncidentSummary): string[] {
  const stats: string[] = [];
  if (inc.case_count) stats.push(`${inc.case_count} confirmed cases`);
  if (inc.deaths) stats.push(`${inc.deaths} death${inc.deaths !== 1 ? 's' : ''}`);
  if (inc.hospitalizations) stats.push(`${inc.hospitalizations} hospitalised`);
  if (inc.countries.length > 1) stats.push(`${inc.countries.length} countries`);
  else if (inc.origin_country) stats.push(inc.origin_country);
  return stats;
}

/** Site-wide strip for ACTIVE crises. Renders nothing when quiet —
 *  most days it should be invisible. Shown on the homepage and /news. */
export default function IncidentBanner() {
  const { data } = useQuery<IncidentSummary[], Error>(
    ['activeMajorIncidents'],
    // Developing crises only: a development (item, count, status) in the
    // last 30 days. With US crises in scope something is open most of the
    // year; the banner is for what is moving now.
    () => fetchIncidents('major', 30),
    { retry: 1, staleTime: 5 * 60 * 1000 },
  );
  if (!data || data.length === 0) return null;

  return (
    <div className="incident-banner-stack">
      {data.slice(0, 2).map(inc => (
        <Link key={inc.key} to={`/incidents/${inc.key}`} className="incident-banner">
          <span className="incident-banner__pulse" aria-hidden="true" />
          <span className="incident-banner__label">Food safety crisis</span>
          <span className="incident-banner__title">{inc.title}</span>
          <span className="incident-banner__stats">
            {incidentStats(inc).join(' · ')}
          </span>
          <span className="incident-banner__cta">Timeline →</span>
        </Link>
      ))}
    </div>
  );
}
